// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
// tslint:disable:no-big-function

import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import {
  IDynamicArrayRenderer,
  IDynamicBindableValue,
  IDynamicInput,
  IDynamicItem,
  IDynamicItemValue,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { mergeMultipleConfigs } from '@epicuro-next/platform/dynamic-components-utils';

import {
  createInstance,
  createInstanceWithChildren,
  getIdProvider,
  getProvider,
} from '../../../utils/dynamic-items-utils';
import { IDynamicEventHandlerWithId } from '../../../utils/eventhandler-from-action';
import { getProductCardIcons } from '../../components';
import {
  getItemMediaPreview,
  TypeForItemMediaPreview,
} from '../../components/item-media-preview';
import { createLink } from '../../components/link';
import { createNoItemsElements } from '../../components/no-items';
import { productDiscountLabel } from '../../components/product-discount-label';
import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
  recommendationItemShownEventHandler,
} from '../../events';
import { ifIsLoading } from '../../loading-state-wrapper-helpers';
import { SingleProductDataType } from '../../types/single-product-data-type';

import { getBrandId } from './bought-this-season/brand-org';

export interface ProductOptions {
  productWidthColumn: number;
  productTitleSize: string;
}

export const propertyNames = {
  productData: 'productData',
  headerText: 'headerText',
  overrideProductTitleFromPropertyKey: 'overrideProductTitleFromPropertyKey',
  brandId: 'brandId',
};

const idPrefix = 'square-panel';
export const ids = {
  loadingSkeletonContainer: 'loading-skeleton-container',
  loadingSkeletonsArrayRenderer: 'loading-skeletons-array-renderer',
  loadingSkeletonItem: 'loading-skeleton-item',
  squarePanelArrayRenderer: `${idPrefix}-array-renderer`,
  squarePanelContainer: `${idPrefix}-container`,
  squarePanelFooterContainer: 'sqaure-panel-footer-container',
  squarePanelHeader: `${idPrefix}-header`,
  squarePanelHeaderContainer: `${idPrefix}-header-container`,
  squarePanelItemContent: `${idPrefix}-item-content`,
  squarePanelItemContentWrapper: `${idPrefix}-item-content-wrapper`,
  squarePanelItemMediaPreview: `${idPrefix}-item-media-preview`,
  squarePanelNoItemsStyleWrapper: `${idPrefix}-no-items-style-wrapper`,
  squarePanelProductsDataContainer: `${idPrefix}-products-data-container`,
  squarePanelRoot: `${idPrefix}-root`,
  squarePanelSeeMoreElementsLink: `${idPrefix}-see-more-elements-link`,
  squarePanelSeeMoreElementsLinkText: `${idPrefix}-see-more-elements-link-text`,
  squarePanelStyleWrapper: `${idPrefix}-style-wrapper`,
};

// tslint:disable-next-line:cognitive-complexity
export function getSquarePanelTemplate(
  isLoadingProviderId: string,
  nrOfProductsToShow = 4,
) {
  const styleWrapper = createInstanceWithChildren({
    id: ids.squarePanelStyleWrapper,
    contentTypeId: 'dynamic-collections-renderer',
    data: {
      backgroundColor: '#EFEFEF',
      globalCss: '',
      rootDynamicClasses: [],
    },
    styles: {
      classes: [
        'square-panel-style-wrapper',
        'd-block',
        'container-fluid',
        'h-100',
      ],
      style: {
        'background-color': {
          provider: 'self',
          property: 'backgroundColor',
        },
        'border-radius': 'var(--border-radius-lg, .25rem)',
      },
      css: { provider: 'self', property: 'globalCss' },
      dynamicClasses: { provider: 'self', property: 'rootDynamicClasses' },
    },
    children: [],
  });

  const root = createInstanceWithChildren({
    id: ids.squarePanelRoot,
    contentTypeId: 'dynamic-collections-renderer',
    data: {
      nrOfProductsToShow,
      seeMoreLink: '' as IDynamicItemValue,
      showProductCardIcons: true,
      brandOrganisationId: {
        provider: 'GlobalStoreProviderService',
        property: {
          path: 'accounts.activeAccount.brandOrganisations',
        },
        filter: function (brandOrganisations: [] | null | undefined): boolean {
          const hasBrandOrganisations =
            !!brandOrganisations && brandOrganisations.length > 0;
          return hasBrandOrganisations;
        }.toString(),
        transform: function (
          brandOrganisations: { brandOrganisationId: string }[],
        ): string {
          return brandOrganisations[0].brandOrganisationId;
        }.toString(),
      },
      overrideProductTitleFromPropertyKey: '',
      noItemsText:
        recommendationsPanelsLocaleKeys.noProductsReturnedFromTheServer,
      noItemsIcon: ['fas', 'tag'],
      singleItemStyle: null,
      showProductIcons: true,
      [propertyNames.brandId]: getBrandId(),
      // TODO @cagin: dynamicThumbnailCount
      dynamicClasses: {
        values: [
          {
            provider: 'self',
            property: 'nrOfProductsToShow',
          },
          {
            provider: 'id:square-panel-products-data-container',
            property: 'productData',
          },
        ],
        transform: function ([nrOfProducts, productData]: [
          number | string,
          SingleProductDataType[],
        ]) {
          let colValue;
          if (+nrOfProducts < 4 || productData.length < 4) {
            colValue = 12;
          } else if (+nrOfProducts >= 9 && productData.length >= 9) {
            colValue = 4;
          } else if (+nrOfProducts >= 2 && productData.length >= 4) {
            colValue = 6;
          } else {
            colValue = 12;
          }
          return [`col-${colValue}`];
        }.toString(),
      },
      // TODO @cagin: dynamicThumbnailCount
      fontSize: {
        values: [
          {
            provider: 'self',
            property: 'nrOfProductsToShow',
          },
          {
            provider: 'id:square-panel-products-data-container',
            property: 'productData',
          },
        ],
        transform: function ([nrOfProducts, productData]: [
          number | string,
          SingleProductDataType[],
        ]) {
          return [
            {
              key: '--text-dynamic-font-size',
              value:
                +nrOfProducts >= 9 && productData.length >= 9
                  ? '0.75rem'
                  : 'inherit',
            },
          ];
        }.toString(),
      },
    },
    styles: {
      classes: ['row', 'h-100', 'align-content-start', 'pb-4'],
    },
    children: [],
    events: undefined as IDynamicItem['events'],
  });
  styleWrapper.children.push(root);

  const headerText = createInstance({
    id: ids.squarePanelHeader,
    contentTypeId: 'dynamic-elements-simple-text',
    data: {
      headerText: undefined as unknown as IDynamicInput, // This should be overriden in what uses this template.
    },
    inputs: {
      text: {
        provider: 'self',
        property: propertyNames.headerText,
      },
    },
    styles: {
      classes: ['h2', 'text-truncate', 'd-block', 'mb-0'],
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
        '--text-dynamic-font-size': '1.5rem',
      },
    },
  });

  const headerContainer = createInstanceWithChildren({
    id: ids.squarePanelHeaderContainer,
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['d-block', 'col-12', 'text-center', 'pt-1'],
    },
    children: [headerText],
  });

  root.children.push(headerContainer);

  const loadingSkeletonItem: IDynamicItem = {
    id: ids.loadingSkeletonItem,
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      style: {
        'aspect-ratio': '1',
      },
      dynamicClasses: getIdProvider(
        ids.loadingSkeletonsArrayRenderer,
        'skeletonItemClasses',
      ),
      classes: ['d-block', 'mb-1'],
    },
    children: [
      {
        contentTypeId: 'dynamic-collections-renderer',
        styles: {
          style: {
            'border-radius': 'var(--border-radius-lg, .25rem)',
            'background-color': '#f5f5f5',
          },
          classes: ['w-100', 'h-100', 'd-block'],
        },
      },
    ],
  };

  const loadingSkeletons: IDynamicItem = {
    id: ids.loadingSkeletonsArrayRenderer,
    contentTypeId: 'dynamic-elements-array-renderer',
    styles: {
      classes: ['row'],
    },
    data: {
      item: [{}],
      // TODO @cagin: dynamicThumbnailCount
      nrOfSkeletons: {
        provider: `id:${ids.squarePanelRoot}`,
        property: 'nrOfProductsToShow',
        // follow logic from ids.squarePanelArrayRenderer sliceEnd property
        transform: function (nrOfProducts: string) {
          if (+nrOfProducts < 4) {
            return 1;
          }

          if (+nrOfProducts >= 9) {
            return 9;
          }

          if (+nrOfProducts >= 2) {
            return 4;
          }

          return 1;
        }.toString(),
      },
      // TODO @cagin: dynamicThumbnailCount
      skeletonItemClasses: {
        provider: `self`,
        property: 'nrOfSkeletons',
        transform: function (nrOfSkeletons: number) {
          let colValue;
          if (nrOfSkeletons < 4) {
            colValue = 12;
          } else if (nrOfSkeletons >= 9) {
            colValue = 4;
          } else if (nrOfSkeletons >= 2) {
            colValue = 6;
          } else {
            colValue = 12;
          }
          return [`col-${colValue}`];
        }.toString(),
      },
    },
    inputs: {
      propertyToUse: propertyNames.productData,
      contentType: loadingSkeletonItem,
      items$: {
        values: [
          {
            provider: 'self',
            property: 'item',
          },
          {
            provider: 'self',
            property: 'nrOfSkeletons',
          },
        ],
        transform: function ([item, nrOfSkeletons]: [{}, number]) {
          return Array(nrOfSkeletons).fill(item);
        }.toString(),
      },
    },
  };

  const loadingSkeletonsContainer: IDynamicItem = {
    id: ids.loadingSkeletonContainer,
    contentTypeId: 'dynamic-collections-renderer',
    // For Suggest Provider: recommendations-data
    // For Lists: square-panel-products-data-container, (or merged with square-panel-list-container)
    if: ifIsLoading('id:' + isLoadingProviderId),
    styles: {
      classes: ['container-fluid'],
    },
    children: [loadingSkeletons],
  };

  const container = createInstanceWithChildren({
    id: ids.squarePanelContainer,
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['col'],
    },
    children: [],
  });
  root.children.push(container);

  const productDataContainer = createInstanceWithChildren({
    id: ids.squarePanelProductsDataContainer,
    contentTypeId: 'dynamic-collections-renderer',
    data: {
      panelName: 'square-panel',
      panelStyle: 'panel-style-not-configured-yet',
      productData: undefined as unknown as IDynamicItemValue, // This should be overridden in what uses this template,
      isLoading: {}, // This should be overridden in what uses this template (getDataLoadingProperties, etc),
      hasError: {}, // This should be overridden in what uses this template (getDataLoadingProperties, etc),
      hasData: {},
      panelTrackingId: '',
      hasItems: {
        provider: 'self',
        property: 'productData',
        transform: function (productData: SingleProductDataType[]): boolean {
          return productData !== undefined && productData.length > 0;
        }.toString(),
      },
    },
    children: [],
  });
  root.children.push(productDataContainer);

  const seeMoreLinkContent: IDynamicSimpleText = {
    id: ids.squarePanelSeeMoreElementsLinkText,
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      classes: ['text-primary', 'h3', 'col'],
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: recommendationsPanelsLocaleKeys.panelSeeMoreButton,
    },
  };

  const seeMoreLink = createLink(
    ids.squarePanelSeeMoreElementsLink,
    getProvider(root, 'seeMoreLink'),
    seeMoreLinkContent,
  );

  const panelFooter = createInstanceWithChildren({
    id: ids.squarePanelFooterContainer,
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: [
        'col-12',
        'text-right',
        'square-panel-footer',
        'container-fluid',
      ],
    },
    if: [
      'EQ',
      [
        {
          ...getProvider(root, 'seeMoreLink'),
          transform: function (value: string) {
            return Boolean(value);
          }.toString(),
        },
        true,
      ],
    ],
    children: [seeMoreLink],
  });
  root.children.push(panelFooter);

  const itemContentType = createInstanceWithChildren({
    id: ids.squarePanelItemContent,
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['position-relative', 'd-block'],
      variables: {
        '--product-card-icons-font-size': '0.6rem',
        '--product-card-icons-gender-icon-font-size': '0.75rem',
      },
    },
    data: {
      [propertyNames.productData]: {
        provider: 'parent',
        property: propertyNames.productData,
      },
    },
    children: [],
  });

  const panelNameProvider = getProvider(productDataContainer, 'panelName');
  const panelStyleProvider = getProvider(
    productDataContainer,
    'panelTrackingId',
  );

  const onClickEventHandlers: IDynamicEventHandlerWithId[] = [
    openConfigStripEventHandler('parent', propertyNames.productData),
    recommendationItemClickedEventHandler(
      panelNameProvider,
      panelStyleProvider,
      {
        provider: 'parent',
        property: 'productData',
      },
    ),
  ];

  const onVisibleOnceEventHandlers: IDynamicEventHandlerWithId[] = [
    recommendationItemShownEventHandler(panelNameProvider, panelStyleProvider, {
      provider: 'parent',
      property: 'productData',
    }),
  ];

  const productTitle: IDynamicSimpleText = {
    // id: 'square-panel-item-content-product-title', // Does not work ATM as it is not reindexed because it is not the first child in what the array renderer renders.
    contentTypeId: 'dynamic-elements-simple-text',
    inputs: {
      text: {
        values: [
          {
            provider: 'parent',
            property: propertyNames.productData,
          },
          getProvider(root, 'overrideProductTitleFromPropertyKey'),
        ],

        transform: function ([
          { product },
          overrideProductTitleFromPropertyKey,
        ]: [SingleProductDataType, string | undefined | null]): string {
          let titleToShow = product.articleTitle;
          if (
            overrideProductTitleFromPropertyKey !== undefined &&
            overrideProductTitleFromPropertyKey !== null &&
            Boolean(overrideProductTitleFromPropertyKey) &&
            overrideProductTitleFromPropertyKey in product
          ) {
            const propertyValue = (
              product as unknown as { [key: string]: string }
            )[overrideProductTitleFromPropertyKey];
            if (typeof propertyValue === 'string' && Boolean(propertyValue)) {
              titleToShow = propertyValue;
            }
          }
          return window.neoclesDynamicFramework.recommendationsPanels.methods.decode(
            titleToShow,
          );
        }.toString(),
      },
    },
    styles: {
      classes: [
        'd-block',
        'text-center',
        'h4',
        'mt-1',
        'mb-1',
        'text-truncate',
      ],
      variables: {
        '--text-dynamic-color':
          'var(--panel-product-title-color, var(--dark-mode-gray-600, #414141))',
      },
      dynamicVariables: getProvider(root, 'fontSize'),
    },
    eventHandlers: {
      onClick: onClickEventHandlers,
    },
  };
  itemContentType.children.push(productTitle);

  const mediaProvider: IDynamicBindableValue = {
    provider: 'parent',
    property: propertyNames.productData,
    transform: function (
      productData: SingleProductDataType,
    ): TypeForItemMediaPreview {
      return productData.media;
    }.toString(),
  };

  const itemMediaPreview = {
    ...getItemMediaPreview(ids.squarePanelItemMediaPreview, mediaProvider, {
      onClick: onClickEventHandlers,
      onVisibleOnce: onVisibleOnceEventHandlers,
    }),
  };
  itemMediaPreview.attributes = {
    tabindex: '0',
  };
  if (itemMediaPreview.styles && itemMediaPreview.styles.style)
    itemMediaPreview.styles.style.__multiple__ = {
      provider: 'id:square-panel-root',
      property: 'singleItemStyle',
    };
  itemContentType.children.push(itemMediaPreview);

  const productCardIcons = mergeMultipleConfigs(getProductCardIcons(), [
    {
      id: 'product-card-icons',
      if: [
        'EQ',
        [
          {
            provider: 'id:square-panel-root',
            property: 'showProductCardIcons',
          },
          true,
        ],
      ],
      inputs: {
        product: {
          property: 'productData',
        },
      },
    },
  ])[0] as IDynamicItem;

  itemContentType.children.push(productCardIcons);

  const starIcon = {
    contentTypeId: 'dynamic-elements-icon',
    styles: {
      classes: ['best-seller-icon'],
      style: {
        display: 'none',
      },
    },
    inputs: {
      icon: ['fas', 'star'],
    },
  };

  itemContentType.children.push(starIcon);
  const productCardDiscountLabel: IDynamicItem = mergeMultipleConfigs(
    productDiscountLabel,
    [
      {
        id: 'product-discount-label',
        styles: {
          style: {
            top: '55px',
          },
        },
      },
    ],
  )[0] as IDynamicItem;

  itemContentType.children.push(productCardDiscountLabel);

  const itemContentTypeWrapper = createInstanceWithChildren({
    id: ids.squarePanelItemContentWrapper,
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      dynamicClasses: getProvider(root, 'dynamicClasses'),
      classes: ['d-block', 'mb-1'],
    },
    data: {
      [propertyNames.productData]: {},
    },
    children: [itemContentType],
  });

  const arrayRenderer: IDynamicArrayRenderer = {
    id: ids.squarePanelArrayRenderer,
    contentTypeId: 'dynamic-elements-array-renderer',
    if: ['EQ', [getProvider(productDataContainer, 'hasItems'), true]],
    styles: {
      classes: ['row'],
    },
    inputs: {
      sliceStart: 0,
      sliceEnd: {
        values: [
          {
            provider: 'id:square-panel-root',
            property: 'nrOfProductsToShow',
          },
          {
            provider: 'id:square-panel-products-data-container',
            property: 'productData',
          },
        ],
        transform: function ([nrOfProducts, productData]: [
          number,
          unknown[],
        ]): number {
          if (+nrOfProducts < 4 || productData.length < 4) {
            return 1;
          } else if (+nrOfProducts >= 9 && productData.length >= 9) {
            return 9;
          } else if (+nrOfProducts >= 2 && productData.length >= 4) {
            return 4;
          } else {
            return 1;
          }
        }.toString(),
      },
      propertyToUse: propertyNames.productData,
      contentType: itemContentTypeWrapper,
      items$: getProvider(productDataContainer, 'productData'),
    },
  };
  container.children.push(loadingSkeletonsContainer, arrayRenderer);

  const noItemsElements = createNoItemsElements({
    hasItems: getProvider(productDataContainer, 'hasItems'),
    icon: getProvider(root, 'noItemsIcon'),
    text: getProvider(root, 'noItemsText'),
  });

  noItemsElements.container.styles.style['margin-top'] = '40%';
  container.children.push(noItemsElements.container);

  return {
    arrayRenderer,
    container,
    headerText,
    headerContainer,
    itemContentTypeWrapper,
    itemContentType,
    itemMediaPreview,
    onClickEventHandlers,
    onVisibleOnceEventHandlers,
    noItemsElements,
    productDataContainer,
    productTitle,
    root,
    seeMoreLink,
    styleWrapper,
  };
}
